import { ErrorBox as EhrBox } from "@metriport/shared-internal";
import axios from "axios";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export function getClientId(practiceFusionEnv: string): string {
  const env = practiceFusionEnv.replace("-", "_").toUpperCase();
  return process.env[`REACT_APP_PRACTICEFUSION_CLIENT_ID_${env}`]!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
}
export function getRedirectUri(): string {
  return process.env.REACT_APP_PRACTICEFUSION_REDIRECT_URI!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
}

// Clear all cookies before OAuth redirect to prevent WAF blocking
function clearAllCookies(): void {
  document.cookie.split(";").forEach(cookie => {
    const cookieName = cookie.split("=")[0]?.trim();
    if (!cookieName) return;
    // Clear cookies set with .metriport.com domain
    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.metriport.com`;
    // Clear cookies set on current host only
    document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  });
}

const scopes = [
  "fhirUser",
  "launch",
  "openid",
  "calendar:r_events_v1",
  "calendar:r_events_v2",
  // PracticeFusion App registration does not support write scopes
  // "user/AllergyIntolerance.c",
  // "user/Condition.c",
  // "user/MedicationRequest.c",
  "user/AllergyIntolerance.rs",
  "user/CarePlan.rs",
  "user/CareTeam.rs",
  "user/Condition.rs",
  "user/Coverage.rs",
  "user/Device.rs",
  "user/DiagnosticReport.rs",
  "user/DocumentReference.rs",
  "user/Encounter.rs",
  "user/Goal.rs",
  "user/Group.rs",
  "user/Immunization.rs",
  "user/Location.rs",
  "user/MedicationDispense.rs",
  "user/MedicationRequest.rs",
  "user/Observation.rs",
  "user/Organization.rs",
  "user/Patient.rs",
  "user/Practitioner.rs",
  "user/Procedure.rs",
  "user/Provenance.rs",
  "user/RelatedPerson.rs",
  "user/ServiceRequest.rs",
  "user/Specimen.rs",
  // TODO: ENG-1889
  // "user/Medication.read",
];

export default function LaunchPage() {
  const location = useLocation();
  const inputParams = new URLSearchParams(location.search);
  const iss = inputParams.get("iss");
  const launch = inputParams.get("launch");

  useEffect(() => {
    async function discoverAndRedirect() {
      const discoveryUrl = `${iss}/.well-known/smart-configuration`;
      const response = await axios.get(discoveryUrl);
      const config = response.data;
      const authorizationUrl = config.authorization_endpoint;
      const tokenUrl = config.token_endpoint;
      const issuer = config.issuer;
      const practiceFusionEnv = getPracticeFusionEnvFromIssuer(issuer);
      // PracticeFusion either not encoding state properly or intentionally strip {}!
      // Base64 encode to avoid URL encoding issues with curly braces
      const state = btoa(JSON.stringify({ tokenUrl, practiceFusionEnv }));
      const redirectParams = new URLSearchParams();
      redirectParams.append("response_type", "code");
      redirectParams.append("scope", scopes.join(" "));
      redirectParams.append("client_id", getClientId(practiceFusionEnv));
      redirectParams.append("redirect_uri", getRedirectUri());
      redirectParams.append("aud", iss ?? "temp");
      redirectParams.append("state", state);
      redirectParams.append("launch", launch ?? "temp");
      const redirectUrl = `${authorizationUrl}?${redirectParams.toString()}`;
      clearAllCookies();
      window.location.replace(redirectUrl);
    }
    discoverAndRedirect();
  }, [iss, launch]);

  return <EhrBox>Launching...</EhrBox>;
}

const practiceFusionEnvironments = new Set([
  "int-api", // - dev
  "qa-api", // - staging
  "rc-api",
  "stg-api",
  "pte-api",
  "api", // - prod
]);

function getPracticeFusionEnvFromIssuer(issuer: string): string {
  const url = new URL(issuer);
  const env = url.hostname.split(".")[0];
  if (env && practiceFusionEnvironments.has(env)) {
    return env;
  } else {
    throw new Error(`Unknown environment extracted from issuer: ${issuer}`);
  }
}
